import { StickyBucketServiceSync, type StickyAssignmentsDocument } from '@growthbook/growthbook'
import { setCookie } from '@/js/services/Utils'

// Growthbook's BrowserCookieStickyBucketService writes one cookie per hash
// attribute as `<prefix><attribute>||<value>` with a JSON body. Matching that
// layout keeps these cookies readable by a stock SDK and the DevTools extension,
// and makes swapping in their class a drop-in if we ever add js-cookie.
const PREFIX = 'gbStickyBuckets__'

// Growthbook's own default.
const EXP_DAYS = 180

// Browsers silently reject cookies over ~4KB, which would un-stick the visitor
// with no signal. Skip the write so the last valid assignment survives.
const MAX_COOKIE_BYTES = 3800

/**
 * Splits a cookie string into a name → value map, decoding each value on its own
 * so an encoded `;` in one value can't split the next pair.
 */
export function parseCookies(raw: string): Record<string, string> {
  const out: Record<string, string> = {}
  if (!raw) {
    return out
  }

  raw.split(';').forEach(pair => {
    const eq = pair.indexOf('=')
    if (eq < 1) {
      return
    }
    const name = pair.slice(0, eq).trim()
    try {
      out[name] = decodeURIComponent(pair.slice(eq + 1).trim())
    } catch (e) {
      out[name] = pair.slice(eq + 1).trim()
    }
  })

  return out
}

export type StickyBucketOptions = {
  prefix?: string
  /** Read from this jar instead of `document.cookie`, so tests can supply one directly. */
  cookies?: Record<string, string>
  expDays?: number
}

/**
 * Browser-side sticky bucket storage.
 *
 * SSR passes no service at all: renders are CDN-cached and bucket on a throwaway
 * per-render id (see FeatureFlags.getHashId), so there is no visitor identity for
 * an assignment to be keyed by.
 *
 * Must be sync — FeatureFlags hydrates Growthbook with `initSync`, which rejects
 * the async services they ship (LocalStorage, Redis) with a console error.
 */
export class CookieStickyBucketService extends StickyBucketServiceSync {
  private cookies?: Record<string, string>

  private expDays: number

  constructor(opts: StickyBucketOptions = {}) {
    super({ prefix: opts.prefix ?? PREFIX })

    this.cookies = opts.cookies
    this.expDays = opts.expDays ?? EXP_DAYS
  }

  private read(key: string): string | undefined {
    if (this.cookies) {
      return this.cookies[key]
    }

    if (typeof document === 'undefined') {
      return undefined
    }

    return parseCookies(document.cookie)[key]
  }

  getAssignmentsSync(attributeName: string, attributeValue: string): StickyAssignmentsDocument | null {
    try {
      const data = JSON.parse(this.read(this.getKey(attributeName, attributeValue)) || '{}')

      // Same shape check Growthbook's cookie service applies, so a half-written
      // or hand-edited cookie is ignored rather than trusted.
      if (data.attributeName && data.attributeValue && data.assignments) {
        return data as StickyAssignmentsDocument
      }
    } catch (e) {
      // Malformed cookie — fall through and re-bucket from the hash.
    }

    return null
  }

  saveAssignmentsSync(doc: StickyAssignmentsDocument): void {
    const key = this.getKey(doc.attributeName, doc.attributeValue)
    const value = encodeURIComponent(JSON.stringify(doc))

    if (key.length + value.length > MAX_COOKIE_BYTES) {
      return
    }

    setCookie(key, value, this.expDays)
  }
}
